import { motion } from 'framer-motion';
import { Check, Zap, ArrowRight } from 'lucide-react';

export const Pricing = () => {
  const features = [
    'Unlimited WhatsApp messages',
    'Meeting scheduling & calendar sync',
    'Smart reminders & notifications',
    'Document summarisation',
    'English, Hindi & Hinglish support',
    '24/7 availability',
    'Priority customer support',
    'No app installation needed',
  ];

  return (
    <section id="pricing" className="py-24 lg:py-32 bg-slate-50/50 dark:bg-slate-900/50" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#25D366] font-semibold text-sm uppercase tracking-wider">Pricing</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white">
            Less than your daily chai.
            <br />
            <span className="text-slate-500 dark:text-slate-400">₹199 per month.</span>
          </h2>
        </motion.div>

        {/* Pricing Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-lg mx-auto"
        >
          <div className="relative">
            {/* Glow Effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-[#25D366] to-emerald-400 rounded-3xl blur-lg opacity-30" />
            
            {/* Card */}
            <div className="relative bg-white dark:bg-slate-800 rounded-3xl p-8 lg:p-10 border border-slate-200 dark:border-slate-700 shadow-xl">
              {/* Badge */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-[#25D366] text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                  14 Days Free Trial
                </span>
              </div>

              {/* Plan Name */}
              <div className="text-center mb-8 pt-4">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Pro Plan</h3>
                <p className="text-slate-600 dark:text-slate-400">Everything you need to stay productive</p>
              </div>

              {/* Price */}
              <div className="text-center mb-8">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-2xl font-bold text-slate-500 dark:text-slate-400">₹</span>
                  <span className="text-6xl font-extrabold text-slate-900 dark:text-white">199</span>
                  <span className="text-slate-500 dark:text-slate-400">/month</span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                  Billed monthly · Cancel anytime
                </p>
              </div>

              {/* Features */}
              <div className="space-y-4 mb-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#25D366]/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-[#25D366]" />
                    </div>
                    <span className="text-slate-700 dark:text-slate-300">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <a
                href="https://wa.me/919981361957?text=Hi%20AssistlyAI!"
                target="_blank"
                rel="noopener noreferrer"
                className="group w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white py-4 rounded-full text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-green-500/30"
                data-testid="pricing-cta"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>

              {/* Trust Line */}
              <div className="flex items-center justify-center gap-2 mt-6 text-sm text-slate-500 dark:text-slate-400">
                <Zap className="w-4 h-4 text-[#25D366]" />
                <span>No credit card required · Setup in 3 minutes</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom Note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center text-slate-500 dark:text-slate-400 mt-12 max-w-md mx-auto"
        >
          Need a custom plan for your team? <a href="#" className="text-[#25D366] hover:underline font-medium">Contact us</a>
        </motion.p>
      </div>
    </section>
  );
};
